import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import ActionCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/ActionCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import TimeCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/TimeCell";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getROMRowValues } from "@/encounters/components/EncounterLabor/utils/labor";

interface ROMRowProps {
  time: string;
  observation: Observation;
  showNumbersOnly: boolean;
  showPreviousVersions?: boolean;
  isViewOnly?: boolean;
}

export default function ROMRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  isViewOnly = false,
}: ROMRowProps) {
  const { method, confirmedBy, fluidColor, notes, hasAnswers, hasNotes } = useMemo(
    () => getROMRowValues(observation),
    [observation],
  );

  const previousVersions = hasPreviousVersions(observation);

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <TimeCell time={time} rowSpan={1} isViewOnly={isViewOnly} observation={observation} />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Rupture of Membranes:</TextWrapper>
            <List fz="sm" withPadding>
              {method && (
                <List.Item>
                  <strong>Method:</strong> {method}
                </List.Item>
              )}
              {confirmedBy && (
                <List.Item>
                  <strong>Confirmed By:</strong> {confirmedBy}
                </List.Item>
              )}
              {fluidColor && (
                <List.Item>
                  <strong>Fluid Color:</strong> {fluidColor}
                </List.Item>
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            ROM Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            rowComponent={ROMRow}
            observation={observation}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
      {showPreviousVersions && !isViewOnly && <ActionCell rowSpan={1} observation={observation} />}
    </Table.Tr>
  );
}
